<template>
	<Transition name="fade-in">
		<div class="page-content" :class="{ 'native-device': isNativeDevice, 'is-inner-page': isInnerPage }">
			<!-- Desktop Header -->
			<Header v-if="!isMobileDevice && route.name !== 'Watch'" />
			<!-- Mobile Header with safe area support -->
			<HeaderNav
				@mounted="headerNavMounted = true"
				v-if="isMobileDevice && route.name !== 'Watch' && !isNativeDevice"
			/>
			<!-- <AppLogo v-if="isLoading" /> -->
			<div
				:class="{
					'router-container main-container':
						!route.name ||
						route.name === 'Root' ||
						route.name === 'Home' ||
						route.name === 'SearchPage' ||
						route.name === 'MoviesPage' ||
						route.name === 'InnerMovie' ||
						route.name === 'InnerSeries' ||
						route.name === 'CategoryPage' ||
						route.name === 'SeriesPage',
				}"
			>
				<div
					:class="{
						'main-view':
							!route.name ||
							route.name === 'Home' ||
							route.name === 'SeriesPage' ||
							route.name === 'MoviesPage' ||
							route.name === 'InnerMovie' ||
							route.name === 'SearchPage' ||
							route.name === 'CategoryPage' ||
							route.name === 'InnerSeries',
						'movie-inner': route.name === 'InnerMovie' || route.name === 'InnerSeries',
					}"
				>
					<PullToRefresh v-if="isMobileDevice && route.name !== 'Watch'" @refresh="handlePageRefresh" />
					<RouterView :key="$route.path" v-if="!isOffline || !isMobileDevice" />
					<div v-else-if="isMobileDevice && isOffline" class="offline-message">
						<IconNoWifi />
						<h2>{{ $t("noConnection") }}</h2>
						<p>{{ $t("noConnectionDescription") }}</p>
					</div>

					<Footer v-if="route.name !== 'Watch' && !isOffline && route.name !== 'PromoPage'" />
				</div>
			</div>

			<Toaster />
			<!-- Mobile navigation for native devices only -->
			<BottomNav v-if="!isOffline && isMobileDevice && route.name !== 'Watch'" />
			<client-only v-if="route.name !== 'Watch' && !isNativeDevice">
				<cookieconsent></cookieconsent>
			</client-only>
		</div>
	</Transition>
</template>

<script setup lang="ts">
	// import PullToRefresh from "./components/PullToRefresh.vue"
	import PullToRefresh from "./components/PullToRefresh.vue"
	import Header from "./components/Header.vue"
	import HeaderNav from "./components/HeaderNav.vue"
	import AppLogo from "./views/AppLogo.vue"
	import BottomNav from "./components/BottomNav.vue"
	import { computed, ref, onMounted, onUnmounted, nextTick, ComputedRef, watchEffect } from "vue"
	import Footer from "./components/Footer.vue"
	import { useUserStore } from "./stores/user"
	import { useDeviceManager } from "./stores/deviceManager"
	import { useDeepLink } from "./composables/useDeepLink"
	import { useRoute } from "vue-router"
	import { useCurrentUser } from "./composables/useCurrentUser"
	import { useI18n } from "vue-i18n"
	import { isNativeDevice, appVersion, url } from "./helper"
	import { StatusBar, Style } from "@capacitor/status-bar"
	import { Toaster } from "vue-sonner"
	import { Network } from "@capacitor/network"
	import IconNoWifi from "./icons/IconNoWifi.vue"
	import cookieconsent from "./components/cookieconsent.vue"
	import { Capacitor } from "@capacitor/core"
	import { SplashScreen } from "@capacitor/splash-screen"
	import { useRouter } from "vue-router"
	import { set } from "@vueuse/core"
	const router = useRouter()
	const route = useRoute()
	const userStore = useUserStore()
	const deviceManager = useDeviceManager()
	const { initializeDeepLinkListener, removeDeepLinkListener } = useDeepLink()
	const { locale } = useI18n()
	const isOffline = ref(false)
	const { fetchCurrentUser } = useCurrentUser(locale)
	const headerNavMounted = ref(false)
	let handler
	const isMobileDevice = computed(
		() =>
			deviceManager.operatingSystem === "ios" ||
			deviceManager.operatingSystem === "android" ||
			Capacitor.isNativePlatform()
		// () => deviceManager.deviceInfo.platform !== "web"
	)

	watchEffect(() => {
		if (Capacitor.isNativePlatform() && headerNavMounted.value) {
			// Hide splash screen when app is ready
			// if (Capacitor.isNativePlatform()) {
			SplashScreen.hide()
			// }
		}
	})

	const isInnerPage = computed(() => {
		return (
			route.name &&
			route.name !== "Home" &&
			route.name !== "SearchPage" &&
			route.name !== "Profile" &&
			route.name !== "MoviesPage" &&
			route.name !== "SeriesPage"
		)
	})

	const isLoggedIn: ComputedRef<boolean> = computed(
		(): boolean => userStore.state.isLoggedIn && userStore.state.selected_profile
	)

	const handlePageRefresh = async () => {
		// Refresh the current route
		window.location.reload()
	}

	onMounted(async () => {
		// localStorage.setItem("hasLoaded", false)
		// const isLoading = ref(localStorage.getItem("hasLoaded"))

		Network.getStatus().then((status) => {
			isOffline.value = !status.connected
		})

		handler = Network.addListener("networkStatusChange", (status) => {
			isOffline.value = !status.connected
		})

		// Initialize device and safe area
		await deviceManager.initializeDevice()
		await deviceManager.initializeSafeArea()

		try {
			await fetchCurrentUser()
		} catch (error) {
			console.error("Error checking network status:", error)
		}

		// Initialize deep link listener for OAuth callbacks
		initializeDeepLinkListener()
		document.documentElement.lang = locale.value
	})

	onUnmounted(async () => {
		// handler.remove()
		// Clean up deep link listeners

		await removeDeepLinkListener()
	})

	// Maybe deprecate this since we're no longer using the "Splash Animation"
	setTimeout(() => {
		userStore.appHasLoaded()
		// localStorage.setItem("hasLoaded", true)
		// isLoading.value = false
	}, 3000)
</script>

<style lang="scss">
	@import url("@/scss/general.scss");
	@import url("@/scss/reset.scss");
	.page-content,
	.main {
		height: 100%;
	}
	.router-container {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: 100%;
		margin-top: -80px;

		@include br.respond-to(br.$mobile-xlarge) {
			margin-top: -var(--safe-area-inset-top);
		}
	}
	.main-view {
		width: 100%;
		// max-width: 1780px;
		max-width: 92.7vw;

		// margin-top: -80px; //todo make this dynamic based on header height with a css variable

		@include br.respond-to(br.$laptop-large) {
			max-width: 1440px;
			// padding-top: 10px;
		}

		&.movie-inner {
			max-width: unset;
		}
		& > .container {
			z-index: 99999;
		}
	}
	body {
		&.is-web {
			// Remove safe area padding for web
			padding: 0;

			.teaser {
				&:focus-visible {
					outline: none !important;
				}
			}
		}

		.teaser-with-number,
		.teaser-small {
			img {
				border: 1px solid rgba(255, 255, 255, 0.1);
				height: 100%;
			}
		}

		&:not(.is-ios) {
			.page-content {
				background: var(--black);
				// &.is-inner-page {
				// 	background: black;
				// }
			}
		}
	}

	html,
	body {
		background-color: var(--black);
	}

	html {
		background-color: var(--black);
	}

	/* Fix white flash on iOS swipe back */
	.is-ios {
		html,
		body,
		#app,
		.page-content {
			background-color: #000000 !important;
		}

		/* Ensure WebView background is black */
		body {
			background: #000000 !important;
			-webkit-background: #000000 !important;
		}
	}

	.main-container {
		@include br.mobile-down {
			padding: 0 10px;
		}
	}

	.offline-message {
		// min-height: 100vh;
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		text-align: center;
		padding: 40px 12px;
		display: flex;
		flex-direction: column;
		gap: 20px;
		align-items: center;
		justify-content: center;
	}
	.cookie-comply__header-description {
		font-size: 16px;
		font-weight: 400;
		color: var(--dark-gray);
	}
	.cookie-comply__header-title {
		font-size: 18px;
		font-weight: 700;
		margin-bottom: 10px;
		color: var(--black);
	}

	.full-bleed {
		position: relative;
		left: 50%;
		right: 50%;
		margin-left: -50vw;
		margin-right: -50vw;
		width: 100vw;
	}

	html,
	body {
		// overscroll-behavior-y: contain; /* disables vertical PTR bounce */
	}

	ol[data-sonner-toaster] {
		* {
			color: #000000;
		}
	}

	.app-version {
		text-align: center;
		width: 100%;
		display: block;
		font-size: 12px;
	}
</style>
